/* eslint-disable react-hooks/exhaustive-deps */
import { Popper, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { FastField, getIn } from "formik";
import React, { forwardRef, useEffect, useMemo, useState } from "react";
import ErrorIcon from "./ErrorIcon";
import RequiredLabel from "./RequiredLabel";
import configDefaultForm from "./configDefault";

const AutoWidthPopper = (props) => {
  const {
    style: { width },
  } = props;
  const style = { minWidth: "fit-content", width };
  return <Popper {...props} style={style} />;
};

const GlobitsAutocompleteTextAndSelect = (props, ref) => (
  <FastField {...props} name={props.name} shouldUpdate={shouldComponentUpdate}>
    {({ field, meta, form }) => (
      <Component
        {...props}
        field={field}
        meta={meta}
        setFieldValue={form.setFieldValue}
        ref={ref}
      />
    )}
  </FastField>
);

const Component = forwardRef((props, ref) => {
  const {
    disabled,
    fullWidth = configDefaultForm.fullWidth,
    label,
    name,
    size = configDefaultForm.size,
    variant = configDefaultForm.variant,
    className = "",
    required,
    requiredLabel,
    onChange,
    readOnly,
    displayLabel = "name",
    options = [],
    field,
    meta,
    setFieldValue,
    multiple,
    getOptionLabel,
    getOptionDisabled,
    getOptionSelected,
    disableTyping = false,
    updateParent,
    placeholder,
    handleFilter,
    autoFocus,
    autoWidthPopper = false,
    colorText,
    tabIndex,
    handleOnClick,
    filterOptionsCustom,
    onKeyDown,
    onInputChange,
    debounceTime = 200,
    ...otherProps
  } = props;
  const [value, setValue] = useState("");
  const [t, setT] = useState(undefined);
  useEffect(() => {
    if (otherProps.value !== value) {
      setValue(otherProps.value);
    }
  }, [otherProps.value]);

  const handleChange = (value, type) => {
    if (type === 1) {
      setValue(value?.vatPercent?.toString() || "");
      if (t) clearTimeout(t);
      // @ts-ignore
      setT(
        setTimeout(() => {
          onChange(value);
        }, debounceTime)
      );
    } else {
      setValue(value);
      if (t) clearTimeout(t);
      // @ts-ignore
      setT(
        setTimeout(() => {
          onInputChange(value);
        }, debounceTime)
      );
    }
  };

  const isError = meta && meta.touched && meta.error;
  const disLabel = useMemo(
    () =>
      label ? (
        <RequiredLabel
          label={label}
          requiredLabel={required || requiredLabel}
        />
      ) : null,
    [label, required, requiredLabel]
  );

  return (
    <>
      {!fullWidth && disLabel && <label htmlFor={name}>{disLabel}</label>}
      <Autocomplete
        {...field}
        {...otherProps}
        ref={ref}
        id={name}
        disabled={disabled || readOnly}
        fullWidth={fullWidth}
        label={displayLabel ? displayLabel : null}
        value={value}
        options={options || []}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : option?.vatPercent?.toString() || ""
        }
        onChange={(event, value) => {
          handleChange(value, 1);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.stopPropagation();
            event.preventDefault();

            return true;
          }
          return true;
        }}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            label={fullWidth && disLabel ? disLabel : null}
            variant={variant}
            placeholder={placeholder}
            InputLabelProps={{
              htmlFor: name,
              shrink: true,
            }}
            onChange={(event) => {
              let value = event.target.value;
              handleChange(value, 2);
            }}
            PopperComponent={autoWidthPopper ? AutoWidthPopper : undefined}
            onClick={handleOnClick && handleOnClick}
            error={isError}
            helperText={fullWidth && isError ? meta.error : ""}
            inputProps={{
              ...params.inputProps,
              readOnly: readOnly || disableTyping,
              style: {
                color: colorText,
              },
              tabIndex: tabIndex,
            }}
            className={`input-container ${className} ${
              readOnly ? "read-only-autocomplete" : ""
            }`}
            autoFocus={autoFocus || false}
          />
        )}
      />
      {isError && !fullWidth && <ErrorIcon helperText={meta.error} />}
    </>
  );
});

const shouldComponentUpdate = (nextProps, currentProps) =>
  nextProps.name !== currentProps.name ||
  nextProps.value !== currentProps.value ||
  nextProps.handleChange !== currentProps.handleChange ||
  nextProps.onInputChange !== currentProps.onInputChange ||
  nextProps.onChange !== currentProps.onChange ||
  nextProps.label !== currentProps.label ||
  nextProps.required !== currentProps.required ||
  nextProps.disabled !== currentProps.disabled ||
  nextProps.readOnly !== currentProps.readOnly ||
  nextProps.options !== currentProps.options ||
  nextProps.disableTyping !== currentProps.disableTyping ||
  nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
  Object.keys(nextProps).length !== Object.keys(currentProps).length ||
  getIn(nextProps.formik.values, currentProps.name) !==
    getIn(currentProps.formik.values, currentProps.name) ||
  getIn(nextProps.formik.errors, currentProps.name) !==
    getIn(currentProps.formik.errors, currentProps.name) ||
  getIn(nextProps.formik.touched, currentProps.name) !==
    getIn(currentProps.formik.touched, currentProps.name);

export default React.memo(forwardRef(GlobitsAutocompleteTextAndSelect));
